import { Suspense, useState, useTransition } from "react";
import {
  AHQueryHandlerLoading,
  AHSeparator,
  AHText,
  Box,
  Input,
  ListItem,
} from "arkh";
import { ActivityIndicator, FlatList, StyleSheet, View } from "react-native";
import { graphql, useLazyLoadQuery } from "react-relay";
import { PlaceSearchFieldQuery } from "./__generated__/PlaceSearchFieldQuery.graphql";
import { useDebouncedCallback } from "use-debounce";
import { FormattedMessage, useIntl } from "react-intl";

export type SelectedPlace = {
  placeId: string;
  address: string;
};

const QUERY = graphql`
  query PlaceSearchFieldQuery($query: String!) {
    searchPlaces(query: $query) {
      id
      displayName
      formattedAddress
    }
  }
`;

function SearchInput(props: {
  onSearch(query: string): void;
  isPending: boolean;
}) {
  const [value, setValue] = useState("");
  const search = useDebouncedCallback(props.onSearch, 500, { maxWait: 1000 });
  const intl = useIntl();

  return (
    <View>
      <Input
        value={value}
        onChangeText={(text) => {
          setValue(text);
          search(text);
        }}
        placeholder={intl.formatMessage({
          defaultMessage: "Search address...",
          description: "PlaceSearchField search input placeholder",
        })}
        space="medium"
        cornerRadius="large"
      />
      {props.isPending && (
        <View style={searchInputStyles.spinner}>
          <ActivityIndicator size="small" />
        </View>
      )}
    </View>
  );
}

const searchInputStyles = StyleSheet.create({
  spinner: {
    position: "absolute",
    right: 16,
    top: 0,
    bottom: 0,
    justifyContent: "center",
  },
});

function SearchResults(props: {
  query: string;
  onSelect: (place: SelectedPlace) => void;
}) {
  const data = useLazyLoadQuery<PlaceSearchFieldQuery>(QUERY, {
    query: props.query,
  });

  return (
    <Box spaceVertical="small">
      <FlatList
        data={data.searchPlaces}
        ItemSeparatorComponent={AHSeparator}
        renderItem={({ item: place, index }) => (
          <ListItem
            isFirst={index === 0}
            isLast={data.searchPlaces.length - 1 === index}
            onPress={() => {
              props.onSelect({
                placeId: place.id,
                address: place.formattedAddress ?? place.displayName ?? "",
              });
            }}
          >
            <ListItem.Title>{place.displayName}</ListItem.Title>
            <ListItem.SubTitle>{place.formattedAddress}</ListItem.SubTitle>
          </ListItem>
        )}
      />
    </Box>
  );
}

export function PlaceSearchField(props: {
  selectedPlace: SelectedPlace | null;
  onSelectPlace: (place: SelectedPlace | null) => void;
}) {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  if (props.selectedPlace) {
    return (
      <ListItem
        isFree
        border="border-primary"
        onPress={() => props.onSelectPlace(null)}
        right={
          <ListItem.DetailActionText>
            <AHText type="subtitle" color="text-secondary">
              <FormattedMessage
                defaultMessage="Change"
                description="PlaceSearchField change button"
              />
            </AHText>
          </ListItem.DetailActionText>
        }
      >
        <ListItem.Title>{props.selectedPlace.address}</ListItem.Title>
      </ListItem>
    );
  }

  return (
    <Box>
      <SearchInput
        isPending={isPending}
        onSearch={(query) => {
          if (query.length === 0) return;
          startTransition(() => {
            setSearchQuery(query);
          });
        }}
      />
      <Suspense fallback={<AHQueryHandlerLoading />}>
        {searchQuery ? (
          <SearchResults query={searchQuery} onSelect={props.onSelectPlace} />
        ) : null}
      </Suspense>
    </Box>
  );
}
